import React, { useCallback, useMemo, useState } from 'react';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { DataSetCompositeReportsConfigComponent } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeReportsConfigComponent';
import { CompositeDataSetRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetRenameColumnsRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { z } from 'zod';
import { DataSetCompositeRecipeDataWizardTracker } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { DataSetCompositeRecipeDataWizardTrackerItem } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { useDataSetCompositeRecipeDataWizardTracker } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useCrudOperations } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-crud-operations';
import { DataSetCompositeCompleteIndicator } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeCompleteIndicator';
import { DataSetCompositeReportsFinalReviewComponent } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeReportsFinalReviewComponent';
import { UseGenerateReportsInsightsPayload } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-generate-reports-insights';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

const RenameColumnsConfigStep = z.enum([
  'reports',
  'renaming',
  'final-review',
  'complete',
]);

type RenameColumnsConfigStep = z.infer<typeof RenameColumnsConfigStep>;

type PendingReportColumnRenaming = {
  reportName: string;
  from: string;
  to: string;
};

const convertPendingReportsToColumnRenames = (
  pendingReportColumnRenamings: PendingReportColumnRenaming[],
) => {
  const groupByReportName: Record<
    string,
    {
      from: string;
      to: string;
    }[]
  > = {};

  pendingReportColumnRenamings.forEach((report) => {
    if (!groupByReportName[report.reportName]) {
      groupByReportName[report.reportName] = [];
    }
    groupByReportName[report.reportName].push({
      from: report.from,
      to: report.to,
    });
  });

  return Object.entries(groupByReportName).map(([reportName, columns]) => ({
    reportName,
    columnRenames: columns,
  }));
};

export const DataSetCompositeRecipeRenameColumnsConfigComponent = ({
  config,
  setConfig,
  insightsPayload,
}: {
  config: CompositeDataSetRenameColumnsRecipe;
  setConfig: (config: CompositeDataSetRecipe) => void;
  insightsPayload: UseGenerateReportsInsightsPayload;
}) => {
  const language = useLanguage();

  const stepItems: DataSetCompositeRecipeDataWizardTrackerItem<RenameColumnsConfigStep>[] =
    useMemo(() => {
      return [
        { value: 'reports', display: 'Reports' },
        { value: 'renaming', display: 'Renaming' },
        { value: 'final-review', display: 'Final Review' },
        { value: 'complete', display: 'Complete' },
      ];
    }, []);

  const [pendingReportColumnRenamings, setPendingReportColumnRenamings] =
    useState<PendingReportColumnRenaming[]>(
      config.renamingReportsConfigs.flatMap((report) => {
        return report.columnRenames.map((columnRename) => ({
          reportName: report.reportName,
          from: columnRename.from,
          to: columnRename.to,
        }));
      }),
    );

  const crudOperationsPayload = useCrudOperations({
    items: pendingReportColumnRenamings,
    setItems: setPendingReportColumnRenamings,
  });

  const onComplete = useCallback(() => {
    const convertedPendingReportsToColumnRenames =
      convertPendingReportsToColumnRenames(pendingReportColumnRenamings);

    setConfig({
      ...config,
      renamingReportsConfigs: convertedPendingReportsToColumnRenames,
    });
  }, [pendingReportColumnRenamings, config, setConfig]);

  const onNext = useCallback(
    (currentItem: RenameColumnsConfigStep) => {
      if (currentItem === 'final-review') {
        onComplete();
      }

      return true;
    },
    [onComplete],
  );

  const dataWizardPayload =
    useDataSetCompositeRecipeDataWizardTracker<RenameColumnsConfigStep>({
      items: stepItems,
      onNext,
    });

  const currentUiToShow = useMemo(() => {
    if (dataWizardPayload.selectedItem === null) {
      return null;
    }

    const convertedPendingReportsToColumnRenames =
      convertPendingReportsToColumnRenames(pendingReportColumnRenamings);

    switch (dataWizardPayload.selectedItem) {
      case 'reports':
        return (
          <DataSetCompositeReportsConfigComponent
            reports={config.reports}
            setReports={(reports) => setConfig({ ...config, reports })}
            insightsPayload={insightsPayload}
          />
        );
      case 'renaming':
        return (
          <Spacing size={'lg'} direction={'vertical'}>
            <Button
              className={'bg-white rounded-md'}
              variant={'outline'}
              size={'small'}
              behavior={{
                type: 'onClick',
                onClick: () => {
                  crudOperationsPayload.onAdd({
                    reportName: '',
                    from: '',
                    to: '',
                  });
                },
              }}
              text={'Add Renaming'}
            />
            <Table
              columnDefinitions={[
                {
                  id: 'reportName',
                  header: () => <div>Report</div>,
                  cell: ({ item, rowIndex }) => (
                    <Box>
                      <input
                        className="form-input"
                        value={item.reportName}
                        onChange={(e) => {
                          crudOperationsPayload.onEdit(rowIndex, {
                            reportName: e.target.value,
                          });
                        }}
                      />
                    </Box>
                  ),
                },
                {
                  id: 'from',
                  header: () => <div>From</div>,
                  cell: ({ item, rowIndex }) => (
                    <Box>
                      <input
                        className="form-input"
                        value={item.from}
                        onChange={(e) => {
                          crudOperationsPayload.onEdit(rowIndex, {
                            from: e.target.value,
                          });
                        }}
                      />
                    </Box>
                  ),
                },
                {
                  id: 'to',
                  header: () => <div>To</div>,
                  cell: ({ item, rowIndex }) => (
                    <Box>
                      <input
                        className="form-input"
                        value={item.to}
                        onChange={(e) => {
                          crudOperationsPayload.onEdit(rowIndex, {
                            to: e.target.value,
                          });
                        }}
                      />
                    </Box>
                  ),
                },
                {
                  id: 'delete',
                  header: () => <div>Delete</div>,
                  cell: ({ rowIndex }) => (
                    <Box>
                      <Button
                        variant={'primary'}
                        size={'small'}
                        behavior={{
                          type: 'onClick',
                          onClick: () => {
                            crudOperationsPayload.onDelete(rowIndex);
                          },
                        }}
                        text={'Delete'}
                      />
                    </Box>
                  ),
                },
              ]}
              items={pendingReportColumnRenamings}
            />
          </Spacing>
        );
      case 'final-review':
        return (
          <Spacing size={'md'} direction={'vertical'}>
            <DataSetCompositeReportsFinalReviewComponent
              reports={config.reports}
            />
            <Box pl={'xs'}>
              <S2Typography
                variant={'headline-5-alt'}
                translatedContent={language.dangerousUserInput({
                  value: `Renamings`,
                })}
              />
            </Box>
            {convertedPendingReportsToColumnRenames.map((report, index) => {
              return (
                <Box pl={'md'} key={index}>
                  <Spacing size={'md'} direction={'vertical'}>
                    <S2Typography
                      variant={'headline-6-alt'}
                      translatedContent={language.dangerousUserInput({
                        value: report.reportName,
                      })}
                    />
                    {report.columnRenames.map((columnRename, key) => {
                      return (
                        <Box pl={'lg'} key={key}>
                          <S2Typography
                            variant={'headline-7-alt'}
                            translatedContent={language.dangerousUserInput({
                              value: `${columnRename.from} -> ${columnRename.to}`,
                            })}
                          />
                        </Box>
                      );
                    })}
                  </Spacing>
                </Box>
              );
            })}
          </Spacing>
        );
      case 'complete':
        return <DataSetCompositeCompleteIndicator />;
      default:
        throw guardExhaustive(dataWizardPayload.selectedItem);
    }
  }, [
    dataWizardPayload.selectedItem,
    pendingReportColumnRenamings,
    config,
    insightsPayload,
    language,
    setConfig,
    crudOperationsPayload,
  ]);

  return (
    <Spacing size={'lg'} direction={'vertical'}>
      <DataSetCompositeRecipeDataWizardTracker payload={dataWizardPayload} />
      {currentUiToShow}
      {dataWizardPayload.paginationButtons}
    </Spacing>
  );
};
